import { useMemo } from 'react';

import { PeerTeam } from '@givebacks/api/store';
import {
  CellContext,
  createColumnHelper,
  useCrowdFundingTranslations,
  useManagementTranslations,
} from 'core/Hooks';
import { centsToDollar, columnsCreator, toTitleCase } from 'core/Utils';
import { Flex, Stack, Text } from 'ui';

import { CrowdFundingAvatar } from '../../../../../../../components/CrowdFundingAvatar/CrowdFundingAvatar';
import { useAthonActivityUnits } from '../../../../../hooks/useAthonActivityUnits';
import { PrizesReportTableInstance } from '../instance';
import { TeamRowActions } from '../rowActions';

const columnHelper = createColumnHelper<PeerTeam>();

const TeamNameHeader = () => {
  const { table } = PrizesReportTableInstance.useContext();
  const { causeId, campaignId } = table.options.meta;
  const { t } = useManagementTranslations({ storeId: causeId, storeItemId: campaignId });
  return <>{t('team')} Name</>;
};

const ParticipantsHeader = () => {
  const { table } = PrizesReportTableInstance.useContext();
  const { causeId, campaignId } = table.options.meta;
  const { t } = useManagementTranslations({ storeId: causeId, storeItemId: campaignId });
  return <># of {t('participants')}</>;
};

const DonationsHeader = () => {
  const { table } = PrizesReportTableInstance.useContext();
  const { causeId } = table.options.meta;
  const { t } = useCrowdFundingTranslations(causeId);
  return <># of {t('Donations')}</>;
};

const ActivityHeader = () => {
  const units = useAthonActivityUnits();

  return units?.unitsPlural ? <># of {toTitleCase(units?.unitsPlural)}</> : null;
};

const ActivityCell = (props: CellContext<PeerTeam, unknown>) => {
  const units = useAthonActivityUnits();

  return <>{units?.pluralize(props.row.original.activity ?? 0)}</>;
};

const TeamGoalHeader = () => {
  const { table } = PrizesReportTableInstance.useContext();
  const { causeId, campaignId } = table.options.meta;
  const { t } = useManagementTranslations({ storeId: causeId, storeItemId: campaignId });
  return <>{t('team')} Goal</>;
};

export const PrizesReportTableColumnsTeams = [
  PrizesReportTableInstance.rowSelectionColumn(),
  columnHelper.accessor('name', {
    header: () => <TeamNameHeader />,
    id: 'name',
    cell: (props) => (
      <Flex align="center" gap={10} mih={34}>
        {/* the above mih holds space for a missing email in the Stack below */}
        <CrowdFundingAvatar
          isLoading={false}
          name={props.row.original.name}
          img={props.row.original.image?.url}
          size={32}
          peerType="teams"
        />
        <Stack gap={0}>
          <Text fz="xs">{props.row.original.name}</Text>
          <Text fz="xxxs">{props.row.original.email}</Text>
        </Stack>
      </Flex>
    ),
    size: 294,
  }),
  columnHelper.accessor((row) => row.participants_count, {
    header: () => <ParticipantsHeader />,
    id: 'participants_count',
    size: 100,
  }),
  columnHelper.accessor((row) => row.number_of_donations ?? '0', {
    header: () => <DonationsHeader />,
    id: 'donations_count',
    meta: {
      sortKey: 'donations_count',
    },
    size: 100,
  }),
  columnHelper.accessor((row) => row.activity ?? '0', {
    header: () => <ActivityHeader />,
    id: 'activity',
    cell: (props) => <ActivityCell {...props} />,
    size: 100,
  }),
  columnHelper.accessor(
    (row) =>
      row?.prize_reports?.reduce((acc, curr) => {
        if (curr.earned) {
          return acc + 1;
        }
        return acc;
      }, 0),
    {
      header: '# of Prizes Earned',
      id: 'prizes_earned',
      enableSorting: false,
      size: 100,
    }
  ),
  columnHelper.accessor((row) => centsToDollar(Number(row.goal)), {
    header: () => <TeamGoalHeader />,
    id: 'goal',
    size: 100,
  }),
  columnHelper.accessor((row) => centsToDollar(row.amount_raised), {
    header: 'Amount Raised',
    meta: {
      sortKey: 'credits_amount',
    },
    size: 100,
  }),
  columnHelper.display({
    id: 'actions',
    cell: TeamRowActions,
    size: 40,
  }),
];

const exportColumnsCreator = columnsCreator<PeerTeam>();
export const useExportTeamPrizeColumns = ({
  campaignType,
  activityUnits,
  isGoalRequested,
}: {
  campaignType?: string;
  activityUnits?: string;
  isGoalRequested?: boolean;
}) => {
  const { table } = PrizesReportTableInstance.useContext();
  const { causeId, campaignId } = table.options.meta;
  const { t } = useManagementTranslations({ storeId: causeId, storeItemId: campaignId });
  return useMemo(
    () => [
      exportColumnsCreator.create({
        header: `${t('team')} Name`,
        cell: (row) => row.name,
      }),
      exportColumnsCreator.create({
        header: `${t('team')} Email`,
        cell: (row) => row.email,
      }),
      exportColumnsCreator.create({
        header: `# of ${t('participants')}`,
        cell: (row) => row.participants_count,
      }),
      exportColumnsCreator.create({
        header: '# of Donations',
        cell: (row) => row.number_of_donations ?? '0',
      }),
      exportColumnsCreator.create({
        header: `# of ${toTitleCase(activityUnits ?? 'Activity')}`,
        cell: (row) => row.activity ?? '0',
        visible: campaignType === 'athon',
      }),
      exportColumnsCreator.create({
        header: 'Prizes Earned',
        cell: (row) =>
          row.prize_reports?.reduce((acc, curr) => {
            if (curr.earned) {
              return acc + 1;
            }
            return acc;
          }, 0),
      }),
      exportColumnsCreator.create({
        header: `${t('team')} Goal`,
        cell: (row) => centsToDollar(row.goal),
        visible: isGoalRequested,
      }),
      exportColumnsCreator.create({
        header: 'Amount Raised',
        cell: (row) => centsToDollar(row.amount_raised),
      }),
      exportColumnsCreator.create({
        header: `${t('team')} Page Link`,
        cell: (row) => row.link_url,
      }),
    ],
    [campaignType, activityUnits, isGoalRequested, t]
  );
};
